import { Component, inject, Input, OnInit } from '@angular/core';
import { ShowPerformance } from '../../../models/showperformance.model';
import { UserService } from '../../../services/user.service';
import { User } from '../../../models/user.model';
import { TemplatesAdminService } from '../../admin/templates/templates-admin.service';
import { ShowService } from '../../show.service';

export interface DynamicCrewMember {
  label: string;
  userName: string;
  hasEarlyShift: boolean;
  isFromShowreport: boolean;
}

@Component({
    selector: 'app-performance-crew-list',
    templateUrl: './performance-crew-list.component.html',
    styleUrls: ['./performance-crew-list.component.scss'],
    standalone: false
})
export class PerformanceCrewListComponent implements OnInit {

  @Input() public performance: ShowPerformance;

  private userService = inject(UserService);
  private templatesService = inject(TemplatesAdminService);
  private showService = inject(ShowService);
  private users: User[] = [];

  public dynamicCrewMembers: DynamicCrewMember[] = [];
  public hasDynamicTemplate = false;

  /**
   * Async work in ngOnInit (users + crew resolution) means the first
   * render runs with an empty dynamicCrewMembers list. Without this
   * flag, the template's @else branch flashed the legacy 5-role
   * hardcoded list for non-legacy shows during the loading window —
   * users on the dashboard saw a "wrong" crew for a moment before it
   * snapped to the real dynamic list. Templates check this flag to
   * render a calm "Crew laden…" placeholder instead.
   */
  public isLoading = true;

  async ngOnInit(): Promise<void> {
    try {
      // Load users for resolving USER_SELECT field values
      this.users = await this.userService.getAllUsers();
      await this.buildDynamicCrewList();
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Check if this show uses legacy showreport format.
   *
   * @deprecated Legacy crew rendering was removed from the template;
   * this method is unreferenced by callers in this component and is
   * scheduled for removal next sprint. The 5-role hardcoded list
   * (Stagemanager / Showcaller / Floortechnician / Troubleshooter /
   * Toneelmeester / Meeloop) it gated is gone — every show goes
   * through {@link #buildDynamicCrewList} now.
   */
  public isLegacyShow(): boolean {
    return this.performance?.show?.useLegacyShowreport ?? false;
  }

  /**
   * Whether we have dynamic crew data ready to render. Intentionally
   * does NOT check {@link #isLegacyShow}; the template branches on
   * legacy independently. Returning {@code false} when not legacy
   * means "no dynamic data yet" — the template should fall through
   * to an empty state, never to the legacy hardcoded list.
   */
  public shouldShowDynamicCrew(): boolean {
    return this.hasDynamicTemplate || this.dynamicCrewMembers.length > 0;
  }

  /**
   * Whether the component should render anything at all. Drives the
   * host-element collapse: when this returns {@code false} the whole
   * Crew section (its title and body) is hidden so the dashboard card
   * doesn't show an empty "Crew" heading for shows that don't have
   * any crew configured.
   *
   * Loading counts as content on purpose — the section appears
   * immediately with a "Crew laden…" placeholder so the layout
   * doesn't shift when data lands.
   */
  public get hasContent(): boolean {
    return this.isLoading || this.shouldShowDynamicCrew();
  }

  /**
   * Build the list of crew members from dynamic field values, crew assignments, or template.
   */
  private async buildDynamicCrewList(): Promise<void> {
    if (this.isLegacyShow()) {
      this.dynamicCrewMembers = [];
      this.hasDynamicTemplate = false;
      return;
    }

    const showreport = this.performance?.showreport;

    // Check crewAssignments first (preferred for dynamic showreports)
    const crewAssignments = showreport?.crewAssignments || [];
    if (crewAssignments.length > 0) {
      this.hasDynamicTemplate = true;
      this.dynamicCrewMembers = crewAssignments.map(assignment => ({
        label: assignment.roleTypeName || 'Crew',
        userName: assignment.userFullName || this.resolveUserName(String(assignment.userId)),
        hasEarlyShift: false,
        isFromShowreport: true
      }));
      return;
    }

    // Check fieldValues for USER_SELECT or CREW_MEMBER fields
    const fieldValues = showreport?.fieldValues || [];
    if (fieldValues.length > 0) {
      const crewFields = fieldValues.filter(f =>
        f.fieldType === 'USER_SELECT' || f.fieldType === 'CREW_MEMBER'
      );

      if (crewFields.length > 0) {
        this.hasDynamicTemplate = true;
        this.dynamicCrewMembers = crewFields.map(field => ({
          label: field.label,
          userName: this.resolveUserName(field.value),
          hasEarlyShift: false,
          isFromShowreport: true
        }));
        return;
      }
    }

    // No showreport-driven crew. Fall back to the show's role types so we
    // render one row per defined role with a "-" placeholder. This is the
    // canonical source for shows that use a CREW section (the modern
    // roleTypes-based mechanism), and matches what the showreport-add
    // page renders inside its CREW section dropdowns.
    const showId = this.performance?.show?.id;
    if (!showId) {
      this.dynamicCrewMembers = [];
      this.hasDynamicTemplate = false;
      return;
    }

    try {
      const roleTypes = await this.showService.getRoleTypes(showId);
      if (roleTypes && roleTypes.length > 0) {
        this.hasDynamicTemplate = true;
        this.dynamicCrewMembers = roleTypes.map(rt => ({
          label: rt.displayName,
          userName: '-',
          hasEarlyShift: false,
          isFromShowreport: false
        }));
        return;
      }
    } catch (error) {
      console.warn('[PerformanceCrewListComponent] Could not load role types:', error);
    }

    // Last-resort fallback: legacy USER_SELECT/CREW_MEMBER fields in the
    // template. Only relevant for shows that haven't migrated to the
    // roleTypes-based CREW section.
    try {
      const template = await this.templatesService.getActiveTemplateForShow(showId);
      if (!template) {
        this.dynamicCrewMembers = [];
        this.hasDynamicTemplate = false;
        return;
      }

      const crewFields: { label: string; fieldKey: string }[] = [];
      if (template.fields) {
        template.fields
          .filter(f => f.fieldType === 'USER_SELECT' || f.fieldType === 'CREW_MEMBER')
          .forEach(f => crewFields.push({ label: f.label, fieldKey: f.fieldKey }));
      }
      if (template.sections) {
        template.sections.forEach(section => {
          if (section.fields) {
            section.fields
              .filter(f => f.fieldType === 'USER_SELECT' || f.fieldType === 'CREW_MEMBER')
              .forEach(f => crewFields.push({ label: f.label, fieldKey: f.fieldKey }));
          }
        });
      }

      if (crewFields.length > 0) {
        this.hasDynamicTemplate = true;
        this.dynamicCrewMembers = crewFields.map(field => ({
          label: field.label,
          userName: '-',
          hasEarlyShift: false,
          isFromShowreport: false
        }));
      } else {
        this.hasDynamicTemplate = false;
        this.dynamicCrewMembers = [];
      }
    } catch (error) {
      console.error('[PerformanceCrewListComponent] Error loading template:', error);
      this.dynamicCrewMembers = [];
      this.hasDynamicTemplate = false;
    }
  }

  /**
   * Resolve a user ID to a full name.
   */
  private resolveUserName(userId: string): string {
    if (!userId) { return '-'; }
    const id = parseInt(userId, 10);
    const user = this.users.find(u => u.id === id);
    return user ? `${user.firstName} ${user.lastName}`.trim() : '-';
  }

  // ---------------------------------------------------------------------------
  // Legacy role-name resolvers
  //
  // These resolved the hardcoded 5-role crew list (Stagemanager / Showcaller /
  // Floortechnician / Troubleshooter / Toneelmeester) plus the Meeloop /
  // extra-crew row. The template branch that called them was removed when
  // the dashboard crew list moved fully to the dynamic roleTypes-based path,
  // so they're unreferenced by this component now.
  //
  // Kept @deprecated for one sprint in case anything else picks them up via
  // template references we missed; remove next sprint along with this comment
  // block and the matching `useLegacyShowreport`-flagged code paths.
  // ---------------------------------------------------------------------------

  /** @deprecated See block comment above; remove next sprint. */
  public getToneelmeesterName(): string {
    const fromLog = this.getRoleName('log', 'toneelmeester');
    const fromShowreport = this.getRoleName('showreport', 'toneelmeester');
    const fromScheduledPerformance = this.performance?.scheduledToneelmeester?.getFullName();
    return fromLog || fromShowreport || fromScheduledPerformance || '-';
  }

  /** @deprecated See block comment above; remove next sprint. */
  public getStagemanagerName(): string {
    const fromScheduledPerformance: string | null = this.performance?.scheduledStagemanager?.getFullName();

    return (
      this.getRoleName('showreport', 'stagemanager') || // Prioriteit 1: showreport
      fromScheduledPerformance ||                     // Prioriteit 2: scheduledStagemanager
      (this.performance?.log ? '-' : '-')              // Prioriteit 3: log aanwezig -> '-'; anders lege string
    );
  }

  /** @deprecated See block comment above; remove next sprint. */
  public getRoleName(roleField: string, roleType: string): string {
    const role = this.performance?.[roleField]?.[roleType];
    return this.formatFullName(role);
  }

  /** @deprecated See block comment above; remove next sprint. */
  public formatFullName(person: any): string {
    return `${ person?.firstName || '' } ${ person?.lastName || '' }`.trim();
  }

  /** @deprecated See block comment above; remove next sprint. */
  public getShowcallerName(): string {
    const fromScheduledPerformance = this.performance?.scheduledShowcaller?.getFullName();

    return (
      this.getRoleName('showreport', 'showcaller') || // Prioriteit 1: showreport
      fromScheduledPerformance ||                   // Prioriteit 2: scheduledShowcaller
      '-'                                            // Fallback: lege string
    );
  }

  /** @deprecated See block comment above; remove next sprint. */
  public getExtraCrewName(): string {
    if (!this.performance) {
      console.error('Performance is missing and not optional');
      return;
    }

    const fromScheduledPerformance = this.performance?.extraCrew;

    if (this.performance?.showreport?.extraCrew != null && this.performance?.showreport?.extraCrew.trim().length > 0) {

      return this.performance?.showreport?.extraCrew;
    }

    if (!this.performance?.showreport && fromScheduledPerformance != null) {
      return fromScheduledPerformance;
    }

    return null;

  }

  /** @deprecated See block comment above; remove next sprint. */
  public getTroubleshooterName(): string {
    const fromScheduledPerformance = this.performance?.scheduledTroubleshooter?.getFullName();
    return (
      this.getRoleName('showreport', 'troubleshooter') || // Prioriteit 1: showreport
      fromScheduledPerformance ||                   // Prioriteit 2: fromScheduledPerformance
      '-'                                            // Fallback: lege string
    );

  }

  /** @deprecated See block comment above; remove next sprint. */
  public getFloortechnicianName(): string {
    const fromScheduledPerformance = this.performance?.scheduledFloortechnician?.getFullName();
    return (
      this.getRoleName('showreport', 'floortechnician') || // Prioriteit 1: showreport
      fromScheduledPerformance ||                   // Prioriteit 2: fromScheduledPerformance
      '-'                                            // Fallback: lege string
    );
  }

}
